import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getUser } from "../../../_api/Admin";
import {
    getUserSkills,
    getSkills,
    createSkill,
    updateUserSkills,
    getObjectives,
    createObjective,
    updateObjective,
    deleteObjective,
    getUserTrainings,
    getTrainings,
    attachUserTraining,
    detachUserTraining,
    getInterviews,
} from "../../../_api/Talent";
import { SideBar } from "../../../_helpers/components/SideBar";
import { BackButton } from "../../../_helpers/components/BackButton";

const LEVEL_LABELS = { DEBUTANT: "Débutant", INTERMEDIAIRE: "Intermédiaire", AVANCE: "Avancé", EXPERT: "Expert" };
const STATUS_LABELS = { TODO: "À faire", IN_PROGRESS: "En cours", DONE: "Terminé" };
const TRAINING_STATUS_LABELS = { PLANNED: "Prévue", IN_PROGRESS: "En cours", COMPLETED: "Réalisée" };
const TRAINING_TYPE_LABELS = { INTERNE: "Interne", EXTERNE: "Externe" };
const INTERVIEW_TYPE_LABELS = { ANNUEL: "Annuel", PROFESSIONNEL: "Professionnel", EVAL_360: "Évaluation 360°", RECRUTEMENT: "Recrutement" };

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");

const KpiList = ({ kpis, onAdd, onRemove, onUpdate }) => {
    const list = kpis || [];
    return (
        <div className="space-y-2">
            {list.map((k, i) => (
                <div key={i} className="flex gap-2 items-center">
                    <input
                        type="text"
                        value={k.label}
                        onChange={(e) => onUpdate(i, "label", e.target.value)}
                        placeholder="Ex: CA, Taux satisfaction"
                        className="flex-1 px-2 py-1.5 text-sm border rounded"
                    />
                    <input
                        type="text"
                        value={k.value}
                        onChange={(e) => onUpdate(i, "value", e.target.value)}
                        placeholder="Ex: 10%, 5"
                        className="flex-1 px-2 py-1.5 text-sm border rounded"
                    />
                    <button type="button" onClick={() => onRemove(i)} className="text-red-600 hover:text-red-800 text-sm p-1">×</button>
                </div>
            ))}
            <button type="button" onClick={onAdd} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium">
                + Ajouter un KPI
            </button>
        </div>
    );
};

const LinkIcon = ({ onClick, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className="p-1.5 rounded text-gray-400 hover:text-app-primary hover:bg-gray-100 transition-colors"
    >
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
    </button>
);

const AddIcon = ({ onClick, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className="p-1.5 rounded text-gray-400 hover:text-app-primary hover:bg-gray-100 transition-colors"
    >
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    </button>
);

const SectionCard = ({ title, subtitle, iconPath, gradient, children, compact, headerAction }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className={`${compact ? "p-3" : "p-4"} border-b border-gray-200 flex items-center justify-between`}>
            <div className="flex items-center space-x-2">
                <div className={`flex items-center justify-center ${compact ? "w-8 h-8" : "w-9 h-9"} bg-gradient-to-br ${gradient} rounded-lg shrink-0`}>
                    <svg className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                    </svg>
                </div>
                <div>
                    <h2 className={`${compact ? "text-base" : "text-lg"} font-bold text-gray-900`}>{title}</h2>
                    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </div>
            </div>
            {headerAction}
        </div>
        <div className={compact ? "p-3" : "p-4"}>{children}</div>
    </div>
);

export const CollaborateurDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [skills, setSkills] = useState([]);
    const [allSkills, setAllSkills] = useState([]);
    const [objectives, setObjectives] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [allTrainings, setAllTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [skillForm, setSkillForm] = useState({ talent_skill_id: "", level: "INTERMEDIAIRE" });
    const [objForm, setObjForm] = useState({ title: "", description: "", kpis: [], comments: "", start_date: "", due_date: "" });
    const [trainingForm, setTrainingForm] = useState({ training_id: "", status: "PLANNED" });
    const [editingObjective, setEditingObjective] = useState(null);
    const [editObjForm, setEditObjForm] = useState({});
    const [showSkillModal, setShowSkillModal] = useState(false);
    const [showTrainingModal, setShowTrainingModal] = useState(false);
    const [showAddObjectiveModal, setShowAddObjectiveModal] = useState(false);
    const [newSkillForm, setNewSkillForm] = useState({ name: "", type: "TECHNIQUE", level: "INTERMEDIAIRE" });

    const fetch = () => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            getUser(id),
            getUserSkills(id).catch(() => ({ data: [] })),
            getSkills().catch(() => ({ data: [] })),
            getObjectives({ user_id: id }).catch(() => ({ data: [] })),
            getInterviews({ user_id: id }).catch(() => ({ data: [] })),
            getUserTrainings(id).catch(() => ({ data: [] })),
            getTrainings().catch(() => ({ data: [] })),
        ])
            .then(([uRes, sRes, aRes, oRes, iRes, tRes, trRes]) => {
                setUser(uRes.data);
                setSkills(sRes.data || []);
                setAllSkills(aRes.data || []);
                setObjectives(oRes.data || []);
                setInterviews(iRes.data || []);
                setTrainings(tRes.data || []);
                setAllTrainings(trRes.data || []);
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), [id]);

    const handleAddSkill = (e) => {
        e.preventDefault();
        if (!skillForm.talent_skill_id) return;
        const current = skills.map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
        const updated = [...current, { talent_skill_id: parseInt(skillForm.talent_skill_id), level: skillForm.level }];
        updateUserSkills(id, updated)
            .then(() => {
                toast.success("Compétence ajoutée.");
                setSkillForm({ talent_skill_id: "", level: "INTERMEDIAIRE" });
                setShowSkillModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleCreateAndAssociateSkill = (e) => {
        e.preventDefault();
        if (!newSkillForm.name?.trim()) return;
        createSkill({ name: newSkillForm.name.trim(), type: newSkillForm.type })
            .then((res) => {
                const newSkill = res.data?.data || res.data;
                const skillId = newSkill?.id;
                if (!skillId) {
                    toast.error("Erreur lors de la création.");
                    return;
                }
                const current = skills.map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
                const updated = [...current, { talent_skill_id: skillId, level: newSkillForm.level }];
                return updateUserSkills(id, updated);
            })
            .then(() => {
                toast.success("Compétence créée et associée.");
                setNewSkillForm({ name: "", type: "TECHNIQUE", level: "INTERMEDIAIRE" });
                setShowSkillModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleRemoveSkill = (skillId) => {
        const updated = skills.filter((s) => s.id !== skillId).map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
        updateUserSkills(id, updated)
            .then(() => {
                toast.success("Compétence retirée.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleAddTraining = (e) => {
        e.preventDefault();
        if (!trainingForm.training_id) return;
        attachUserTraining(id, parseInt(trainingForm.training_id), trainingForm.status)
            .then(() => {
                toast.success("Formation associée.");
                setTrainingForm({ training_id: "", status: "PLANNED" });
                setShowTrainingModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleRemoveTraining = (trainingId) => {
        if (!window.confirm("Retirer cette formation du collaborateur ?")) return;
        detachUserTraining(id, trainingId)
            .then(() => {
                toast.success("Formation retirée.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleAddObjective = (e) => {
        e.preventDefault();
        if (!objForm.title.trim()) return;
        const kpis = (objForm.kpis || []).filter((k) => k.label?.trim() && k.value?.trim());
        createObjective({ user_id: id, ...objForm, kpis })
            .then(() => {
                toast.success("Objectif créé.");
                setObjForm({ title: "", description: "", kpis: [], comments: "", start_date: "", due_date: "" });
                setShowAddObjectiveModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleUpdateObjective = (e) => {
        e.preventDefault();
        if (!editingObjective || !editObjForm.title?.trim()) return;
        const kpis = (editObjForm.kpis || []).filter((k) => k.label?.trim() && k.value?.trim());
        updateObjective(editingObjective.id, { ...editObjForm, kpis })
            .then(() => {
                toast.success("Objectif mis à jour.");
                setEditingObjective(null);
                setEditObjForm({});
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleUpdateObjectiveStatus = (objId, status) => {
        updateObjective(objId, { status })
            .then(() => {
                toast.success("Objectif mis à jour.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleDeleteObjective = (objId) => {
        if (!window.confirm("Supprimer cet objectif ?")) return;
        deleteObjective(objId)
            .then(() => {
                toast.success("Objectif supprimé.");
                setEditingObjective(null);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const startEditObjective = (obj) => {
        setEditingObjective(obj);
        setEditObjForm({
            title: obj.title,
            description: obj.description || "",
            kpis: Array.isArray(obj.kpis) && obj.kpis.length > 0 ? [...obj.kpis] : [],
            comments: obj.comments || "",
            start_date: obj.start_date ? obj.start_date.slice(0, 10) : "",
            due_date: obj.due_date ? obj.due_date.slice(0, 10) : "",
        });
    };

    const addKpiToForm = (formKey) => {
        if (formKey === "add") {
            setObjForm((f) => ({ ...f, kpis: [...(f.kpis || []), { label: "", value: "" }] }));
        } else {
            setEditObjForm((f) => ({ ...f, kpis: [...(f.kpis || []), { label: "", value: "" }] }));
        }
    };

    const removeKpiFromForm = (formKey, index) => {
        if (formKey === "add") {
            setObjForm((f) => ({ ...f, kpis: f.kpis.filter((_, i) => i !== index) }));
        } else {
            setEditObjForm((f) => ({ ...f, kpis: f.kpis.filter((_, i) => i !== index) }));
        }
    };

    const updateKpiInForm = (formKey, index, field, val) => {
        if (formKey === "add") {
            setObjForm((f) => ({
                ...f,
                kpis: f.kpis.map((k, i) => (i === index ? { ...k, [field]: val } : k)),
            }));
        } else {
            setEditObjForm((f) => ({
                ...f,
                kpis: f.kpis.map((k, i) => (i === index ? { ...k, [field]: val } : k)),
            }));
        }
    };

    if (loading) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-app-primary mx-auto mb-3"></div>
                    <p className="text-sm text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error || !user) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600 text-sm">{error || "Collaborateur introuvable"}</div>
            </div>
        );
    }

    const availableSkills = allSkills.filter((s) => !skills.some((us) => us.id === s.id));
    const availableTrainings = allTrainings.filter((t) => !trainings.some((ut) => ut.id === t.id));

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                        <BackButton
                            onClick={() => navigate("/talents/collaborateurs")}
                            title="Retour"
                        />
                        <div className="flex items-center space-x-2">
                            <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-app-primary to-app-secondary rounded-full text-white font-semibold text-sm">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">{user.first_name} {user.last_name}</h1>
                                <p className="text-xs text-gray-500">{user.email} · {user.category?.name || "-"}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-4">
                        <SectionCard title="Informations" iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" gradient="from-slate-500 to-slate-600" compact>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><p className="text-gray-500 text-xs">Email</p><p className="font-medium">{user.email}</p></div>
                                <div><p className="text-gray-500 text-xs">Poste</p><p className="font-medium">{user.category?.name || "-"}</p></div>
                            </div>
                        </SectionCard>

                        <SectionCard
                            title="Compétences"
                            subtitle={skills.length ? `${skills.length} compétence${skills.length > 1 ? "s" : ""}` : "Aucune"}
                            iconPath="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                            gradient="from-blue-500 to-blue-600"
                            compact
                            headerAction={<LinkIcon onClick={() => setShowSkillModal(true)} title="Associer une compétence" />}
                        >
                            {skills.length > 0 && (
                                <ul className="space-y-1">
                                    {skills.map((s) => (
                                        <li key={s.id} className="flex justify-between items-center py-1.5 px-2 rounded bg-gray-50 text-sm">
                                            <span>{s.name} <span className="text-gray-500">– {LEVEL_LABELS[s.pivot?.level] || s.pivot?.level}</span></span>
                                            <button onClick={() => handleRemoveSkill(s.id)} className="text-xs text-red-600 hover:text-red-800">Retirer</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(!skills.length || availableSkills.length > 0) && (
                                <p className="text-xs text-gray-500 mt-2">
                                    {availableSkills.length > 0 ? "Cliquez sur l’icône lien pour associer une compétence." : skills.length > 0 ? "Toutes les compétences sont déjà associées." : "Aucune compétence associée."}
                                </p>
                            )}
                        </SectionCard>

                        <SectionCard
                            title="Formations"
                            subtitle={trainings.length ? `${trainings.length} formation${trainings.length > 1 ? "s" : ""}` : "Aucune"}
                            iconPath="M12 6.253v3m0 2.253v3m0-3v3m0-3c-1.658 0-3 1.342-3 3s1.342 3 3 3 3-1.342 3-3v-3m0 0c-1.658 0-3-1.342-3-3s1.342-3 3-3 3 1.342 3 3v3m0-3c-1.658 0-3 1.342-3 3s1.342 3 3 3 3-1.342 3-3v-3"
                            gradient="from-violet-500 to-violet-600"
                            compact
                            headerAction={<LinkIcon onClick={() => setShowTrainingModal(true)} title="Associer une formation" />}
                        >
                            {trainings.length > 0 && (
                                <ul className="space-y-1">
                                    {trainings.map((t) => (
                                        <li key={t.id} className="flex justify-between items-start gap-2 py-1.5 px-2 rounded bg-gray-50 text-sm">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium">{t.name}</span>
                                                    {t.is_certified && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Certifiée</span>}
                                                    <span className="text-gray-500">– {TRAINING_STATUS_LABELS[t.pivot?.status] || t.pivot?.status}</span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {TRAINING_TYPE_LABELS[t.type] || t.type}
                                                    {(t.trainer_user || t.trainer_name) && ` · ${t.trainer_user ? `${t.trainer_user.first_name} ${t.trainer_user.last_name}` : t.trainer_name}`}
                                                </p>
                                            </div>
                                            <button onClick={() => handleRemoveTraining(t.id)} className="text-xs text-red-600 hover:text-red-800 shrink-0">Retirer</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(!trainings.length || availableTrainings.length > 0) && (
                                <p className="text-xs text-gray-500 mt-2">
                                    {availableTrainings.length > 0 ? "Cliquez sur l’icône lien pour associer une formation." : trainings.length > 0 ? "Toutes les formations sont déjà associées." : "Aucune formation associée."}
                                </p>
                            )}
                        </SectionCard>

                        <SectionCard
                            title="Entretiens"
                            subtitle={interviews.length ? `${interviews.length} entretien${interviews.length > 1 ? "s" : ""}` : "Aucun"}
                            iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            gradient="from-emerald-500 to-emerald-600"
                            compact
                            headerAction={
                                <button
                                    type="button"
                                    onClick={() => navigate("/talents/entretiens")}
                                    className="text-xs text-app-primary hover:text-app-secondary font-medium"
                                >
                                    Voir les entretiens →
                                </button>
                            }
                        >
                            {interviews.length > 0 ? (
                                <ul className="space-y-1">
                                    {interviews.map((i) => (
                                        <li
                                            key={i.id}
                                            onClick={() => navigate(`/talents/entretiens/${i.id}`)}
                                            className="flex justify-between items-center py-1.5 px-2 rounded bg-gray-50 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
                                        >
                                            <div>
                                                <span className="font-medium">{INTERVIEW_TYPE_LABELS[i.type] || i.type}</span>
                                                <span className="text-gray-500"> · {formatDate(i.date)}</span>
                                                {i.evaluator && <span className="text-gray-500"> · {i.evaluator.first_name} {i.evaluator.last_name}</span>}
                                            </div>
                                            <span className="text-xs text-app-primary shrink-0">Voir →</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500">Aucun entretien enregistré pour ce collaborateur.</p>
                            )}
                        </SectionCard>
                    </div>

                    <div className="space-y-4">
                        <SectionCard
                            title="Objectifs"
                            subtitle={objectives.length ? `${objectives.filter((o) => o.status !== "DONE").length} en cours` : "Aucun"}
                            iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            gradient="from-emerald-500 to-emerald-600"
                            compact
                            headerAction={<AddIcon onClick={() => setShowAddObjectiveModal(true)} title="Ajouter un objectif" />}
                        >
                            {objectives.length > 0 ? (
                                <ul className="space-y-3">
                                    {objectives.map((o) => (
                                        <li key={o.id} className="rounded-lg bg-gray-50 border border-gray-100 overflow-hidden">
                                            {editingObjective?.id === o.id ? (
                                                <form onSubmit={handleUpdateObjective} className="p-3 space-y-2">
                                                    <input type="text" value={editObjForm.title} onChange={(e) => setEditObjForm((f) => ({ ...f, title: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" required placeholder="Titre" />
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">KPIs</label>
                                                        <KpiList
                                                            kpis={editObjForm.kpis}
                                                            onAdd={() => addKpiToForm("edit")}
                                                            onRemove={(i) => removeKpiFromForm("edit", i)}
                                                            onUpdate={(i, f, v) => updateKpiInForm("edit", i, f, v)}
                                                        />
                                                    </div>
                                                    <textarea value={editObjForm.description} onChange={(e) => setEditObjForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border rounded" placeholder="Description" />
                                                    <textarea value={editObjForm.comments} onChange={(e) => setEditObjForm((f) => ({ ...f, comments: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border rounded" placeholder="Commentaires" />
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="block text-xs text-gray-600 mb-1">Date de l'objectif</label>
                                                            <input type="date" value={editObjForm.start_date} onChange={(e) => setEditObjForm((f) => ({ ...f, start_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-600 mb-1">Date de fin de l'objectif</label>
                                                            <input type="date" value={editObjForm.due_date} onChange={(e) => setEditObjForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between items-center gap-2">
                                                        <div className="flex-1" />
                                                        <div className="flex gap-1">
                                                            <button type="submit" className="px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white">Enregistrer</button>
                                                            <button type="button" onClick={() => setEditingObjective(null)} className="px-2 py-1 text-xs border rounded text-gray-600">Annuler</button>
                                                        </div>
                                                    </div>
                                                </form>
                                            ) : (
                                                <div className="p-3">
                                                    <div className="flex justify-between items-start gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <p className="font-medium text-gray-900">{o.title}</p>
                                                            {Array.isArray(o.kpis) && o.kpis.length > 0 && (
                                                                <div className="mt-1 space-y-0.5">
                                                                    {o.kpis.map((k, i) => (
                                                                        <p key={i} className="text-xs text-emerald-700"><strong>{k.label}</strong> : {k.value}</p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {o.description && <p className="text-sm text-gray-600 mt-1">{o.description}</p>}
                                                            {o.comments && <p className="text-sm text-gray-500 mt-1 italic">Commentaires : {o.comments}</p>}
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {o.start_date && <>Date de l'objectif : {formatDate(o.start_date)}{o.due_date && " · "}</>}
                                                                {o.due_date && <>Date de fin : {formatDate(o.due_date)}{" · "}</>}
                                                                {STATUS_LABELS[o.status] || o.status}
                                                            </p>
                                                        </div>
                                                        <div className="flex gap-1 shrink-0">
                                                            <button onClick={() => startEditObjective(o)} className="text-xs text-gray-600 hover:text-gray-800">Modifier</button>
                                                            {o.status !== "DONE" && <button onClick={() => handleUpdateObjectiveStatus(o.id, "DONE")} className="text-xs text-green-600">Terminer</button>}
                                                            <button onClick={() => handleDeleteObjective(o.id)} className="text-xs text-red-600">Suppr.</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                                    <p className="text-sm text-gray-500">Aucun objectif pour ce collaborateur.</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddObjectiveModal(true)}
                                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-app-primary hover:text-app-secondary"
                                    >
                                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Ajouter un objectif
                                    </button>
                                </div>
                            )}
                        </SectionCard>
                    </div>
                </div>
            </div>

            <SideBar
                title="Associer une compétence"
                content={
                    <div className="space-y-6">
                        {availableSkills.length > 0 && (
                            <form onSubmit={handleAddSkill} className="space-y-4">
                                <p className="text-sm text-gray-600">Choisissez une compétence du référentiel à associer au collaborateur.</p>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Compétence existante</label>
                                    <select
                                        value={skillForm.talent_skill_id}
                                        onChange={(e) => setSkillForm((f) => ({ ...f, talent_skill_id: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    >
                                        <option value="">— Choisir —</option>
                                        {availableSkills.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                                    <select
                                        value={skillForm.level}
                                        onChange={(e) => setSkillForm((f) => ({ ...f, level: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">Associer</button>
                                </div>
                            </form>
                        )}

                        <div className="pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-3">Ou créer une nouvelle compétence</p>
                            <form onSubmit={handleCreateAndAssociateSkill} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Nom</label>
                                    <input
                                        type="text"
                                        value={newSkillForm.name}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, name: e.target.value }))}
                                        placeholder="Ex: Excel, Communication"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
                                    <select
                                        value={newSkillForm.type}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, type: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="TECHNIQUE">Technique</option>
                                        <option value="COMPORTEMENTALE">Comportementale</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Niveau</label>
                                    <select
                                        value={newSkillForm.level}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, level: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">Créer et associer</button>
                                </div>
                            </form>
                        </div>

                        {availableSkills.length === 0 && !newSkillForm.name && (
                            <p className="text-sm text-gray-500">Toutes les compétences existantes sont déjà associées. Créez-en une nouvelle ci-dessous.</p>
                        )}

                        <div className="pt-2">
                            <button type="button" onClick={() => setShowSkillModal(false)} className="text-sm text-gray-500 hover:text-gray-700">Fermer</button>
                        </div>
                    </div>
                }
                isOpen={showSkillModal}
                onClose={() => setShowSkillModal(false)}
            />

            <SideBar
                title="Associer une formation"
                content={
                    availableTrainings.length > 0 ? (
                        <form onSubmit={handleAddTraining} className="space-y-4">
                            <p className="text-sm text-gray-600">Choisissez une formation du catalogue à associer au collaborateur.</p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Formation</label>
                                <select
                                    value={trainingForm.training_id}
                                    onChange={(e) => setTrainingForm((f) => ({ ...f, training_id: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                >
                                    <option value="">— Choisir —</option>
                                    {availableTrainings.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                <select
                                    value={trainingForm.status}
                                    onChange={(e) => setTrainingForm((f) => ({ ...f, status: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    {Object.entries(TRAINING_STATUS_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">Associer</button>
                                <button type="button" onClick={() => setShowTrainingModal(false)} className="px-4 py-2 border rounded text-gray-600">Annuler</button>
                            </div>
                        </form>
                    ) : (
                        <p className="text-sm text-gray-600">Toutes les formations du catalogue sont déjà associées à ce collaborateur.</p>
                    )
                }
                isOpen={showTrainingModal}
                onClose={() => setShowTrainingModal(false)}
            />

            <SideBar
                title="Ajouter un objectif"
                content={
                    <form onSubmit={handleAddObjective} className="space-y-4">
                        <p className="text-sm text-gray-600">Nouvel objectif pour ce collaborateur.</p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                            <input
                                type="text"
                                value={objForm.title}
                                onChange={(e) => setObjForm((f) => ({ ...f, title: e.target.value }))}
                                placeholder="Ex: Atteindre 10 % de croissance"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={objForm.description}
                                onChange={(e) => setObjForm((f) => ({ ...f, description: e.target.value }))}
                                rows={2}
                                placeholder="Description"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">KPIs (label, valeur)</label>
                            <KpiList
                                kpis={objForm.kpis}
                                onAdd={() => addKpiToForm("add")}
                                onRemove={(i) => removeKpiFromForm("add", i)}
                                onUpdate={(i, f, v) => updateKpiInForm("add", i, f, v)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaires</label>
                            <textarea
                                value={objForm.comments}
                                onChange={(e) => setObjForm((f) => ({ ...f, comments: e.target.value }))}
                                rows={1}
                                placeholder="Commentaires"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date de l'objectif</label>
                                <input
                                    type="date"
                                    value={objForm.start_date}
                                    onChange={(e) => setObjForm((f) => ({ ...f, start_date: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin de l'objectif</label>
                                <input
                                    type="date"
                                    value={objForm.due_date}
                                    onChange={(e) => setObjForm((f) => ({ ...f, due_date: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">
                                Ajouter
                            </button>
                            <button type="button" onClick={() => setShowAddObjectiveModal(false)} className="px-4 py-2 border rounded text-gray-600">
                                Annuler
                            </button>
                        </div>
                    </form>
                }
                isOpen={showAddObjectiveModal}
                onClose={() => setShowAddObjectiveModal(false)}
            />
        </div>
    );
};
